import { ENV_VARS } from '@/env';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import { DoubleRightOutlined } from '@ant-design/icons';
import { Button, Col, Form, Row } from 'antd';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as SaveIcon } from '../../../assets/icons/save.svg';
import { APP_ROUTES } from '../../../contants/routes';
import {
  createSpotQuoteInProgress,
  createSpotQuoteReset,
  resetCopyQuoteKey,
  resetCurrentQuoteCarrier,
  resetCurrentQuoteCollaborators,
  resetCurrentQuoteRoutingGuidesParams,
  resetQuoteBidExpirationDate,
  setCurrentQuoteRoutingGuidesParams,
  setQuoteActiveAccordions,
  setQuoteModalBlocker,
} from '../../../redux/common/CommonActions';
import {
  getShipmentDefaultAccessorialsInProgress,
  resetCreateShipmentQuote,
  resetShipmentRateShopList,
} from '../../../redux/shipment/ShipmentActions';
import { getQuotePayloadProductTotals } from '../../../utilz';
import { routeFileSelector } from '../../../utilz/i18n';
import { parseCreateSpotQuoteDataFromFormData } from '../../../utilz/quoteDataManipulation';
import IconButton from '../../components/Buttons/IconButton';
import SvgConverter from '../../components/Common/SvgConverter';
import PrimaryLabel from '../../components/Labels/PrimaryLabel';
import SecondaryLabel from '../../components/Labels/SecondaryLabel';
import Paper from '../../components/Paper/Paper';
import { formsName } from '../Shipment/components/QuoteCard/constants';
import HallCreateGetQuoteTabs from './components/CreateGetQuote/HallCreateGetQuoteTabs';
import { SECTIONS_BY_FORMS, TAB_KEYS } from './constants';

const isHalliburton = ENV_VARS.REACT_APP_THEME === 'halliburton';

// todo GetQuoteScreen for HB
const HallGetQuoteScreen = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { defaultAccessorials } = useSelector((state) => state.shipment);
  const { createSpotQuote, quoteModalBlocker, currentSpotQuoteNoAndTitle, activeAccordion, appLoader } = useSelector(
    (state) => state.common,
  );
  const { selectedCompany } = useSelector((state) => state.auth);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const numberOfTabKeys = Object.keys(TAB_KEYS).length;

  const setActiveAccordion = (value) => {
    const accordions = [];
    if (!Array.isArray(value)) {
      accordions.push(value);
    }

    dispatch(setQuoteActiveAccordions([...accordions, ...value]));
  };

  useEffect(() => {
    dispatch(resetShipmentRateShopList());
  }, [dispatch]);

  useEffect(() => {
    if (!defaultAccessorials?.success && selectedCompany?.companyId) {
      const payload = {
        shipmentKey: 0,
        customerKey: selectedCompany?.companyId,
      };
      dispatch(getShipmentDefaultAccessorialsInProgress(payload));
    }
  }, [defaultAccessorials?.success, dispatch, selectedCompany]);

  useEffect(() => {
    return () => {
      dispatch(resetCreateShipmentQuote());
      dispatch(resetCurrentQuoteRoutingGuidesParams());
      dispatch(resetCurrentQuoteCarrier());
      dispatch(resetCurrentQuoteCollaborators());
      dispatch(resetQuoteBidExpirationDate());
    };
  }, [dispatch]);

  const validateForm = (formInstance, formName) => {
    // biome-ignore lint/suspicious/noAsyncPromiseExecutor: <explanation>
    return new Promise(async (resolve, reject) => {
      try {
        const validationStatus = await formInstance.validateFields();
        resolve({ [formName]: validationStatus });
      } catch (error) {
        reject({ formName, ...error });
      }
    });
  };

  const handleSubmit = async (_triggeredForm, { forms }) => {
    try {
      setIsSubmitting(true);
      const formList = formsName.map((formName) => validateForm(forms[formName], formName));

      const validateResponse = await Promise.all(formList);
      const formResponse = validateResponse.reduce((a, b) => {
        // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
        return { ...a, ...b };
      }, {});

      const isHaveUnsavedFiles = formResponse.documentForm.documents.find((item) => item.isNew && !item?.file);
      if (isHaveUnsavedFiles) {
        showNotification({
          type: NOTIFICATION_TYPES.ERROR,
          message: 'Please complete or cancel adding the file',
        });
        throw { formName: 'documentForm' };
      }

      const footerFields = getQuotePayloadProductTotals(formResponse.productForm.items);
      const createSpotQuoteData = parseCreateSpotQuoteDataFromFormData({
        quoteKey: 123,
        ...footerFields,
        ...formResponse.quoteDetailsForm,
        ...formResponse.shippingTermsSearchForm,
        ...formResponse.notesForm,
        ...formResponse.documentForm,
        stops: formResponse?.stopsForm?.stops,
        products: formResponse?.productForm?.items,
        transportationMode: formResponse?.stopsForm?.transportationMode,
        distance: formResponse?.stopsForm?.distance,
      });
      const documentsPayload = formResponse?.documentForm?.documents?.map((value) => value.formData);

      dispatch(
        createSpotQuoteInProgress({
          quoteDocuments: documentsPayload,
          quote: createSpotQuoteData,
        }),
      );
    } catch (e) {
      setActiveAccordion([SECTIONS_BY_FORMS[e.formName]]);
    } finally {
      dispatch(setQuoteModalBlocker(false));
      setIsSubmitting(false);
    }
  };

  // redirect if the quote is successfully created
  useEffect(() => {
    if (createSpotQuote.success && currentSpotQuoteNoAndTitle.data.quoteNo && !appLoader) {
      dispatch(createSpotQuoteReset());
      dispatch(resetCopyQuoteKey());
      setActiveAccordion([]);
      navigate(
        generatePath(APP_ROUTES.SEARCH_QUOTE_ID, {
          quoteKey: currentSpotQuoteNoAndTitle.data.quoteNo?.slice(5),
        }),
      );
    }
  }, [createSpotQuote.success, currentSpotQuoteNoAndTitle.data.quoteNo, dispatch, navigate, appLoader]);

  const handleFormChanges = (forms) => {
    const stopFormValues = forms.stopsForm.getFieldsValue();
    const shippingTermsFormValues = forms.shippingTermsSearchForm.getFieldsValue();

    dispatch(
      setCurrentQuoteRoutingGuidesParams({
        CountryCode: stopFormValues?.stops?.[0]?.countryCode,
        TransportationMode: stopFormValues?.transportationMode,
        TransportationGroup: shippingTermsFormValues?.transportationGroup,
      }),
    );
  };

  const handleClickExpandCollapse = () => {
    if (activeAccordion.length !== numberOfTabKeys) setActiveAccordion(Object.values(TAB_KEYS));
    else setActiveAccordion([]);
  };

  return (
    <Form.Provider
      onFormFinish={handleSubmit}
      onFormChange={(_, { forms }) => {
        handleFormChanges(forms);

        if (!quoteModalBlocker) {
          dispatch(setQuoteModalBlocker(true));
        }
      }}
    >
      <Row className="flex-direction-column flex-nowrap  h-100">
        <Col className="mb-15" data-testid="get-quote-screen">
          <Paper className="p-15">
            <Row align="middle" justify="space-between" gutter={30}>
              <Col>
                <PrimaryLabel className="text-black">Create Quote</PrimaryLabel>
              </Col>
              <Col>
                <Row gutter={30} align="middle">
                  <Col>
                    <Form name="saveQuoteForm" className="lh-0">
                      <IconButton buttonType="submit" loading={isSubmitting}>
                        <Row gutter={10} align="middle">
                          <Col className="lh-0">
                            <SvgConverter
                              Icon={SaveIcon}
                              width={20}
                              height={20}
                              color={`header ${isHalliburton ? 'hb-icon' : ''}`}
                            />
                          </Col>
                          <Col className="lh-0">
                            <SecondaryLabel className="f-14 f-400">
                              {t('translation:default.buttons.save')}
                            </SecondaryLabel>
                          </Col>
                        </Row>
                      </IconButton>
                    </Form>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Paper>
        </Col>
        <Col>
          <Row className="mb-15">
            <Button
              icon={<DoubleRightOutlined rotate={activeAccordion.length === numberOfTabKeys ? 90 : 0} />}
              type="text"
              onClick={handleClickExpandCollapse}
            >
              {activeAccordion.length === numberOfTabKeys ? 'Collapse All' : 'Expand All'}
            </Button>
          </Row>
        </Col>
        <Col className="flex-1 custom-scroll-bar">
          <HallCreateGetQuoteTabs activeKey={activeAccordion} setActiveKey={setActiveAccordion} />
        </Col>
      </Row>
    </Form.Provider>
  );
};

export default memo(HallGetQuoteScreen);
